import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import IndentDetails from "../IndentPage/ItemsPage/IndentDetails2";
import ItemsTable from "./TL2IT";
import { useSelector } from "react-redux";
import IndentChangesTracker from "../IndentChangesTracker";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const NewL2InvoicingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { row: initialRow } = location.state || {};

  const [row, setRow] = useState(initialRow);
  const [items, setItems] = useState([]);
  const [isDraft, setIsDraft] = useState(true);
  const [error, setError] = useState(null);
  const [isEditable, setIsEditable] = useState(true);
  const [categories, setCategories] = useState([]);
  const currentUserId = useSelector((state) => state.auth.id);
  const [changeLog, setChangeLog] = useState({
    addedItems: [],
    deletedItems: [],
    updatedItems: [],
  });
  const [storedChangeLogs, setStoredChangeLogs] = useState([]);
  const [pageData, setPageData] = useState(null);
  const apiEndpoint = initialRow?.IndentID ? `${API_BASE_URL}/l2page/indents/${initialRow.IndentID}` : null;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const indentResponse = await axios.get(apiEndpoint);
        // Handle both array and single object responses
        const indentData = Array.isArray(indentResponse.data)
          ? indentResponse.data
          : [indentResponse.data];

        const indentsWithItems = await Promise.all(
          indentData.map(async (indent) => {
            if (!indent) return null;
            try {
              const itemsResponse = await axios.get(
                `${API_BASE_URL}/l2page/indentitems/${initialRow.IndentID}`
              );

              // Format the committed date or use current date if null
              const formattedDate = indent.CommitedDate
                ? new Date(indent.CommitedDate).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "2-digit",
                  })
                : new Date().toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "2-digit",
                  });

              return {
                ...indent,
                CommitedDate: formattedDate,
                IndentItems: itemsResponse.data || [],
              };
            } catch (error) {
              console.error("Error fetching items:", error);
              return { ...indent, IndentItems: [] };
            }
          })
        );

        const validIndents = indentsWithItems.filter(Boolean);
        setPageData(validIndents);
        
        // Update the row state with the latest data
        if (validIndents.length > 0) {
          setRow(validIndents[0]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setPageData([]);
      }
    };
    
    if (apiEndpoint) {
      fetchData();
    }
  }, [apiEndpoint]);

  // Add a refresh mechanism when component regains focus
  useEffect(() => {
    const handleFocus = () => {
      if (apiEndpoint) {
        // Refresh data when window regains focus
        const fetchData = async () => {
          try {
            const indentResponse = await axios.get(apiEndpoint);
            const indentData = Array.isArray(indentResponse.data)
              ? indentResponse.data
              : [indentResponse.data];

            if (indentData && indentData.length > 0) {
              setRow(indentData[0]);
            }
          } catch (error) {
            console.error("Error refreshing data:", error);
          }
        };
        fetchData();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [apiEndpoint]);

  // Error logging utility
  const logError = useCallback((context, error) => {
    console.error(`[L2InvoicingPage] ${context}:`, error);
    // Potential integration with error logging service
  }, []);

  // Fetch Indent Details with comprehensive error handling
  const fetchIndentDetails = useCallback(async () => {
    if (!initialRow?.IndentID) {
      setError("No Indent ID provided");
      return null;
    }

    try {
      // Note the change in URL structure
      const response = await axios.get(
        `${API_BASE_URL}/l2page/indents/${initialRow.IndentID}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data) {
        const updatedRow = {
          ...initialRow,
          ...response.data,
          UserID: response.data.UserID,
        };
        setRow(updatedRow);
        return updatedRow;
      }
    } catch (error) {
      logError("Fetch Indent Details", error);

      if (error.response?.status === 404) {
        setError(
          "Indent not found. It may have been deleted or does not exist."
        );
      } else if (error.response?.status === 401) {
        navigate("/login", {
          state: {
            message: "Your session has expired. Please log in again.",
          },
        });
      } else {
        setError("Failed to fetch indent details. Please try again.");
      }
    }
    return null;
  }, [initialRow?.IndentID, navigate, logError]);

  // Fetch stored change logs
  const fetchChangeLogs = useCallback(async () => {
    if (!initialRow?.IndentID) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/change_log/change-logsl2/${initialRow.IndentID}`,
        {
          params: { userId: currentUserId },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setStoredChangeLogs(response.data || []);
    } catch (error) {
      logError("Fetch Change Logs", error);
      if (error.response?.status === 404) {
        setStoredChangeLogs([]);
      }
    }
  }, [initialRow?.IndentID, currentUserId, logError]);

  // Save change log to backend
  const saveChangeLog = useCallback(
    async (changeLogData) => {
      try {
        await axios.post(
          `${API_BASE_URL}/change_log/change-logs`,
          {
            indentId: initialRow.IndentID,
            userId: currentUserId,
            currentlevel: "L2",
            ...changeLogData,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      } catch (error) {
        logError("Save Change Log", error);
        setError("Failed to save change log");
      }
    },
    [initialRow?.IndentID, currentUserId, logError]
  );

  // Fetch draft items
  const fetchDraftItems = useCallback(async () => {
    if (initialRow && initialRow.IndentID) {
      try {
        const response = await axios.get(`${API_BASE_URL}/l2page/indentitems`, {
          params: { indentId: initialRow.IndentID },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        const indentStatus =
          response.data.length > 0
            ? {
                l1a: response.data[0].L1 || 0,
                l1: response.data[0].L1A || 0,
              }
            : { l1a: 0, l1: 0 };

        const draftItems = response.data.map((item) => ({
          indentItemID: item.IndentItemID,
          itemID: item.ItemID,
          itemName: item.ItemName,
          quantity: item.Quantity,
          stock: item.Stock || 0,
          categoryID: item.CategoryID,
          remarks: item.Description,
          promisedDate: item.PromisedDate,
          total: item.Quantity * (item.Stock || 0),
          accountingUnitID: item.AccountingUnitID,
          unitname: item.UnitName,
        }));

        setItems(draftItems);
        setIsDraft(true);

        // Update row status
        setRow(prev => ({
          ...prev,
          L1A: indentStatus.l1a,
          L1: indentStatus.l1,
        }));
      } catch (error) {
        logError("Fetch Draft Items", error);

        if (error.response?.status === 404) {
          setError("No items found for this indent");
        } else if (error.response?.status === 401) {
          navigate("/login", {
            state: {
              message: "Your session has expired. Please log in again.",
            },
          });
        } else {
          setError("Failed to load draft items. Please try again.");
        }
      }
    }
  }, [initialRow, navigate, logError]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const categoryResponse = await axios.get(
        `${API_BASE_URL}/dropdown/mcatdrop`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setCategories(categoryResponse.data || []);
    } catch (error) {
      logError("Fetch Categories", error);
    }
  }, [logError]);

  // Individual fetch effects with stable dependencies
  useEffect(() => {
    if (initialRow?.IndentID) {
      fetchDraftItems();
    }
  }, [initialRow?.IndentID]);

  useEffect(() => {
    if (initialRow?.IndentID) {
      fetchChangeLogs();
    }
  }, [initialRow?.IndentID]);

  useEffect(() => {
    if (initialRow?.IndentID) {
      fetchCategories();
    }
  }, [initialRow?.IndentID]);

  // Item Update Handler
  const handleItemUpdate = useCallback(
    (updateInfo) => {
      setChangeLog((prev) => {
        const updatedLog = { ...prev };

        switch (updateInfo.type) {
          case "add":
            updatedLog.addedItems.push(updateInfo.item);
            saveChangeLog({ type: "add", item: updateInfo.item });
            break;
          case "dele<IndentDetails row={row} />  // Ensure this is how you're passing the propte":
            updatedLog.deletedItems.push(updateInfo.item);
            saveChangeLog({ type: "delete", item: updateInfo.item });
            break;
          case "update":
            updatedLog.updatedItems.push({
              original: items.find(
                (item) => item.itemID === updateInfo.item.itemID
              ),
              updated: updateInfo.item,
            });
            saveChangeLog({
              type: "update",
              original: items.find(
                (item) => item.itemID === updateInfo.item.itemID
              ),
              updated: updateInfo.item,
            });
            break;
          default:
            break;
        }
        return updatedLog;
      });
    },
    [items, saveChangeLog]
  );

  // Initialize page only once when initialRow changes
  useEffect(() => {
    if (!initialRow) {
      setError("No indent selected. Please go back and select an indent.");
      return;
    }
    // The individual fetch effects will handle data loading
  }, [initialRow]);

  // Submission Handler
  const handleSubmit = useCallback(
    async (submissionData) => {
      if (
        !submissionData ||
        !submissionData.items ||
        submissionData.items.length === 0
      ) {
        setError("Please add items before submitting");
        return;
      }

      // Include userId in the submission data
      const dataWithUserId = {
        ...submissionData,
        userId: currentUserId, // Use the existing currentUserId variable
      };

      try {
        const response = await axios.post(
          `${API_BASE_URL}/l2page/indents/submit`,
          dataWithUserId,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.data && response.data.message) {
          setIsDraft(false);
          setIsEditable(false);

          navigate("/L2page", {
            state: {
              message: response.data.message || "Indent submitted successfully",
            },
          });
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Submission failed. Please try again.";

        setError(errorMessage);
        console.error("Submission Error:", error.response?.data || error);
      }
    },
    [navigate, currentUserId]
  );

  // Save Draft Handler
  const handleSaveDraft = useCallback(async () => {
    if (!initialRow || !initialRow.IndentID) {
      setError("No indent selected");
      return;
    }

    if (items.length === 0) {
      setError("Please add items before saving draft");
      return;
    }

    try {
      await axios.put(
        `${API_BASE_URL}/l2page/indents/${initialRow.IndentID}/draft`,
        {
          isDraft: true,
          items: items.map((item) => ({
            itemID: item.itemID,
            quantity: item.quantity,
            remarks: item.remarks,
            categoryID: item.categoryID,
            stock: item.stock,
          })),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      navigate("/L2page", {
        state: {
          message: "Draft saved successfully",
        },
      });
    } catch (error) {
      logError("Save Draft", error);
      setError("Failed to save draft. Please try again.");
    }
  }, [initialRow, items, navigate, logError]);

  const handlePrint = () => {
    window.print();
  };

  if (!initialRow) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <div className="flex-grow container mx-auto p-6 text-center">
          <p className="text-red-500">
            {error ||
              "No indent selected. Please go back and select an indent."}
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header
        exportEndpoint={apiEndpoint}
        importEndpoint={apiEndpoint}
        currentIndentData={pageData}
      />
      <div className="flex-grow container mx-auto">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold underline">L2 Items</h3>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <IndentDetails 
          row={row} 
          onUpdate={(updatedRow) => {
            setRow(updatedRow);
            // Also update pageData if it exists
            if (pageData && pageData.length > 0) {
              const updatedPageData = pageData.map(item => 
                item.IndentID === updatedRow.IndentID ? updatedRow : item
              );
              setPageData(updatedPageData);
            }
          }} 
        />

        {/* Rest of the return remains the same */}
        <ItemsTable
          indentID={initialRow.IndentID}
          items={items}
          onAddItem={(newItem) => setItems((prev) => [...prev, newItem])}
          onDeleteItem={(index) =>
            setItems((prev) => prev.filter((_, i) => i !== index))
          }
          onUpdateItem={handleItemUpdate}
          calculateTotalAmount={() =>
            items.reduce((acc, item) => acc + (item.total || 0), 0).toFixed(2)
          }
          isDraft={isDraft}
          onSubmit={handleSubmit}
          onSaveDraft={handleSaveDraft}
          onPrint={handlePrint}
          isEditable={isEditable}
          indentType={initialRow.TypeName}
          indentStatus={{
            l1a: row?.L1A || 0,
            l1: row?.L1 || 0,
            l2: row?.L2 || 0,
          }}
        />

        <IndentChangesTracker
          indentID={row?.IndentID.toString()}
          changes={changeLog}
          storedChangeLogs={storedChangeLogs}
        />
      </div>
    </div>
  );
};

export default NewL2InvoicingPage;
